/*global $:false, Slick:false */

import down_gif from '@cob/ui-genesis/images/down.gif';

import { format as formatNumber } from '../fields/recordm.fields.number.js';
import { sprintf } from 'sprintf-js';
import tmpl_batch_actions from '../templates/search-definition-batch-actions.dot';

import tmpl_details_and_actions from '../templates/search-definition-result-action-dropdown.dot';
import tmpl_results from '../templates/search-definition-results.dot';
import tmpl_view_dropdown from '../templates/search-definition-view-dropdown.dot';
import tmpl_view_save from '../templates/search-definition-view-save.dot';
import utils from '@cob/ui-genesis/cob.utils.js';

// $container: the jquery HTML wrapper for the enclosing element
//
// confs
//      * `canCreateNewRecord` : If the user can create new records of this definition
//      * `rowsPerPage` : The size of the result (page size)
//      * `columns` : the grid columns
//
// callbacks:
//      * createNewRecordRequested: called when the user wants to create a new record
//      * getIndexedInstanceM(entityId): return the entityM
//      * getInstanceActions(entityId): return list of actions available for this instance
//      * executeInstanceAction(actionKey, instanceKey, instanceId): Execute the specific action on the instance result
//      * canExecuteBatchAction(action, id): Returns true if the user can execute this batch action with this instance id
//      * executeBatchActionOnDataset(bActionKey, instanceKey, indexedInstanceIds): Execute the specific action on the selected results
//      * executeBatchActionOnQuery(bActionKey, query): Execute the specific action on all results from the query
//      * moreRecordsClick: called when the user asks for more search results
//      * columnsVisibilityChanged(columns, selectedResults): called when the user clicks on a view option checkbox or on one of the view options selection buttons.
//      * columnWidthChanged(fieldName, width): called when the user changes a columns's width by draging it to the desired width.
//      * columnOrderChanged(columns): called when the user drags some column(s), changing their order.
//      * saveCurrentView: called when the user clicks the "save view" button.
//      * viewNameConfirmed(name): called after the user inserts the new view name.
//      * deleteView(viewId): called when the user clicks on the "delete view" link.
//      * shareView(viewId): called when the user clicks on the "share view" link.
//      * unshareView(viewId): called when the user clicks on the "unshare view" link.
//      * activateView(viewId, viewLabel): called when the user selects a new view from the available views dropdown.
//      * selectAllViewOptionsRequested: called when the user clicks the "select all" button on the view options list.
//      * unselectAllViewOptionsRequested: called when the user clicks the "remove selection" button on the view options list.
//      * getFieldValue(resultData, fieldDef): called to get the value for each search result cell
//      * onDetailsClicked(instanceId): optional override function for seeing the details inline (used in references)
//      * allowRowSelection: should show the checkboxes to select rows
//      * showDetailsColumns: Should show the Details column
//      * showColumnsSelector: should show the toggle to show/hidde columns
//
const DefinitionSearchResultV = function(core, definitionM, $container, confs, callbacks) {

    var _canCreateNewRecord = confs.canCreateNewRecord || false;
    var _canImportRecords = confs.canImportRecords || false;
    var _canPrint = confs.canPrint || false;

    const _confs = {
      allowRowSelection: true,
      showDetailsColumns: true,
      showColumnsSelector: true
    };

    utils.extend(_confs, confs, true);

    var _$grid,
        _$gridCanvas,
        _$totalRecordsLabel,
        _$massActionsContainer,
        _$batchActionsContainer,
        _$emptyResultMsgContainer,
        _$importBtn,
        _$newRecordBtn,
        _$actionsMsg,
        _$printBtn;

    var _visibleDataLength;

    var _allInstancesSelected = false,
        _batchActions = [],
        _customBatchActions = [];

    var _$viewSaveButton,
        _$viewStatus,
        _$activeView,
        _$viewDropdownList,
        _$viewSaveConfirmDialog,
        _$viewSaveName;

    var _slickgrid,
        _slickCheckboxSelector,
        _slickgridHeaderMenu,
        _slickSelectionModel,
        _printPlugin,
        intersectionObserver;

    var selectEntriesText = core.translateString("recordm.search", "select-entries-to-show-options"),
        noSelectableEntriesText = core.translateString("recordm.search", "no-entries-to-select"),
        noResultsText = core.translateString("recordm.search", "no-results"),
        nextRecordsText = core.translateString("recordm.search", "next_n_records"),
        allActionsText = core.translateString("recordm.search", "action.all"),
        detailsLinkText = core.translateString("recordm.search", "action.details-link"),
        moreBatchActionsText = core.translateString("recordm.search", "action.custom-batch-actions-container");

    var headerMenus = {
        title: core.translateString("recordm.search", "headermenu.title"),
        "select-all": core.translateString("recordm.search", "headermenu.select-all"),
        "unselect-all": core.translateString("recordm.search", "headermenu.unselect-all"),
        "resize-columns": core.translateString("recordm.search", "headermenu.resize-columns"),
        "actions": core.translateString("recordm.search", "headermenu.actions")
    };

    var _viewMenuEntries = [
        {
            title: headerMenus.title,
            disabled: true
        },
        {
            title: headerMenus["select-all"],
            command: "all",
            contentClass: "select-all",
            isSelectable: false,
            iconCssClass: "icon-ok"
        },
        {
            title: headerMenus["unselect-all"],
            command: "none",
            contentClass: "remove-all",
            isSelectable: false,
            iconCssClass: "icon-remove"
        },
        {
            title: headerMenus["resize-columns"],
            command: "resize",
            contentClass: "resize-columns",
            isSelectable: false,
            iconCssClass: "icon-resize-horizontal"
        },
        {
            separator: true
        }
    ];

    var _emptyColumn = {
        id: "placebo",
        name: "",
        resizable: true,
        sortable: false,
        reordable: false
    };

    var _pageSize = confs.rowsPerPage,
        _columns = confs.columns || { all: [], visible: [] },
        _totalResults = 0;

    var _slickGridOptions = {
        // Don't remove rowHeight prop because it is
        // required to calculate the height of the grid canvas.
        rowHeight: 25,
        explicitInitialization: true,
        enableCellNavigation: false,
        forceFitColumns: true,
        enableTextSelectionOnCells: true,
        autoHeight: confs.autoHeight || false,
        dataItemColumnValueExtractor: callbacks.getFieldValue,
        syncColumnCellResize: true,
        asyncPostRenderDelay: 0,
        enableAsyncPostRender: true
    };

    var _populateVariables = function() {
        _$grid = $(".grid", $container);
        _$totalRecordsLabel = $(".js-total-records", $container);
        _$emptyResultMsgContainer = $(".empty", $container);

        _$viewSaveButton = $(".js-view-save-button", $container);
        _$viewStatus = $(".js-view-status", $container);
        _$activeView = $(".js-active-view", $container);
        _$viewDropdownList = $(".js-view-dropdown-list", $container);

        _$massActionsContainer = $('.js-mass-actions', $container);
        _$batchActionsContainer = $('.js-batch-actions', $container);

        _$actionsMsg = $('.js-actions-msg', _$massActionsContainer);
        _$importBtn = $('.js-import-btn', _$massActionsContainer);
        _$newRecordBtn = $('.js-new-record', _$massActionsContainer);
        _$printBtn = $('.js-print-btn', _$massActionsContainer);
    };

    var _getColumnsInSlickgridFormat = function() {

        var slickGridCols = [], // an array like [{ id: .., name: ..., field: ..., width: ... }, ...],
            thereAreResizableColumns = false;

        var headerMenuitems;

        if(_confs.allowRowSelection) {
          // Place the checkbox column at the start
          var rowSelectionColumnDefinition = _slickCheckboxSelector.getColumnDefinition();
          rowSelectionColumnDefinition.reordable = false;

          slickGridCols.push(rowSelectionColumnDefinition);
        }

        var sortedColumns = _columns.all
            .sort(function(col1, col2) {
                return col1.label.toLowerCase().localeCompare(col2.label.toLowerCase());

            }).filter(function(col) {
                return col.visibleInSearchResults;

            }).map(function(col) {
                col.visible = false;

                _columns.visible.forEach(function(visibleItem) {
                    if (visibleItem.fieldName === col.fieldName) {
                        col.visible = true;
                    }
                });

                thereAreResizableColumns = col.resizable || thereAreResizableColumns;

                return {
                    title: col.label,
                    command: col.fieldName,
                    isSelectable: true,
                    isChecked: col.visible
                };
            });

        headerMenuitems = _viewMenuEntries.concat(sortedColumns);

        slickGridCols = slickGridCols.concat(
            _columns.visible.map(_buildSlickGridColumnDefinitionObject)
        );

        //If there are no visible columns or
        //if all the visible columns are not resizable
        //we add an empty column to keep the grid width unchanged
        if (_columns.visible.length === 0 || !thereAreResizableColumns) {
            slickGridCols.push(_emptyColumn);
        }

        // Place the edit action column at the end
      const actionsColumn = {
        id: "actions",
        headerCssClass: "center-aligned",
        cssClass: "center-aligned entry-actions",
        reordable: false,
        header: {
          menu: {
            items: headerMenuitems,
            alignRight: true
          }
        },
        formatter: function(row, cell, value, columnDef, dataContext) {
          if (!_confs.showDetailsColumns) return;

          return tmpl_details_and_actions({
              indexedInstanceM: callbacks.getIndexedInstanceM(dataContext.id),
              actions: callbacks.getInstanceActions(dataContext.id),
              detailsLinkText: detailsLinkText
            }
          );
        },
        printable: false
      };

      if (_confs.showDetailsColumns) {
        actionsColumn.name = headerMenus.actions;
        actionsColumn.width = 85;
        actionsColumn.minWidth = 100;
        actionsColumn.maxWidth = 100;

      } else if (_confs.showColumnsSelector) {
        actionsColumn.width = 15;
        actionsColumn.minWidth = 15;
        actionsColumn.maxWidth = 15;
      }

      if(!_confs.showColumnsSelector){
        delete actionsColumn.header;
      }

      if (_confs.showDetailsColumns || _confs.showColumnsSelector) {
        slickGridCols.push(actionsColumn);
      }

      return slickGridCols;
    };

    var _buildSlickGridColumnDefinitionObject = function(col) {
        var columnDefinition = {
            id: col.fieldName.replaceAll(/[^A-Za-z0-9-_:.]/g, "_"),
            name: col.labelIcon ? col.labelIcon : col.label,
            field: col.fieldName,
            fieldDefId:col.fieldDefId,
            width: col.columnWidth,
            resizable: col.resizable,
            sortable: col.sortable,
            entityField: true,
            fieldDefDescription: col.fieldDefDescription,
            printable:col.printable
        };

        // no dataContext recebemos o doc ES completo
        columnDefinition.formatter = function(row, cellCol, value, colDef, dataContext) {
            let content = value;
            if (col.formatter && typeof col.formatter === 'function') {
                content = col.formatter(value, dataContext, colDef, core.getLocale());
            }
            const customFormatters = core.getColumnCustomizations(definitionM.getName(), columnDefinition.name);
            if(customFormatters && customFormatters.length > 0){
                // cria uma div que embrulha o conteúdo da célula para podermos manipular o estilo dessa div
                // (não temos acesso à célula do slickgrid, ainda não está criada neste momento)
                // na práctiva acrescenta um div nova entre o que era a célula do Slickgrid e o conteúdo
                const cell = document.createElement("div")
                // tentamos manipular o estilo desta div para neutralizar o da célula envolvente
                // foi o estilo que resultou melhor nos testes
                cell.style = "margin:-4px -2px -4px -4px;padding:4px 1px 1px 4px;height:100%;overflow: inherit;";
                cell.innerHTML = content;

                for(const formatter of customFormatters) {
                    if (typeof formatter === 'function') formatter(cell, dataContext, colDef);

                    else if (typeof formatter.classes === 'function'){
                       const classes = formatter.classes(cell.innerHTML.trim(), dataContext, colDef);
                       if (typeof classes === 'string') cell.classList.add(classes);
                       else if(Array.isArray(classes)) classes.forEach((c) => cell.classList.add(c))
                    }
                }
                content = cell.outerHTML;
            }
            return content
        }

        columnDefinition.headerCssClass = "center-aligned";

        if (col.align === 'center') {
            columnDefinition.cssClass = "center-aligned";

        } else if (col.align === 'right') {
            columnDefinition.cssClass = "right-aligned";
        } else {
            columnDefinition.cssClass = "left-aligned";
        }

        return columnDefinition;
    };

    var _initSlickgridPlugins = function() {
        _slickgridHeaderMenu = new Slick.Plugins.HeaderMenu({
           buttonImage: down_gif,
           cobAppInstanceId: core.getAppInstanceId()
        });

        _slickgridHeaderMenu.onCommand.subscribe(function(e, args) {

            var changedColumns = [];

            if (args.command === "resize") {

                _slickGridOptions.forceFitColumns = !_slickGridOptions.forceFitColumns;
                _slickgrid.setOptions(_slickGridOptions);


                // Reset the columns
                _slickgrid.setColumns(_getColumnsInSlickgridFormat());

                // Reset the selected rows since slickgrid doesn't do it itself
                _slickSelectionModel.setSelectedRows(_slickSelectionModel.getSelectedRows());

            } else{

                if (args.command === "all") {

                    _columns.all.forEach(function(column) {
                        changedColumns.push({fieldName: column.fieldName, visibility: true});
                    });

                    _updateMenuItemsSelection(true);

                } else if (args.command === "none") {

                    _columns.all.forEach(function(column) {
                        changedColumns.push({fieldName: column.fieldName, visibility: false});
                    });

                    _updateMenuItemsSelection(false);

                } else {

                    changedColumns.push({fieldName: args.command, visibility: args.item.isChecked});

                    _slickgridHeaderMenu.setMenuItemSelection(args.command, args.item.isChecked);
                }

                callbacks.columnsVisibilityChanged(changedColumns, _getSelectedResultsIds());
            }


        });

        _slickCheckboxSelector = new Slick.CheckboxSelectColumn({
            cssClass: "slick-cell-checkboxsel"
        });

        _printPlugin = new Slick.Plugins.Print();
    };

    var _buildSlickGrid = function() {

        _initSlickgridPlugins();

        _slickSelectionModel = new Slick.RowSelectionModel({
            selectActiveRow: false
        });

        _slickgrid = new Slick.Grid(_$grid, [], _getColumnsInSlickgridFormat(), _slickGridOptions);

        // Can only find the grid-canvas after slickgrid has been initialized
        _$gridCanvas = $(".grid-canvas", _$grid);

        _slickgrid.setSelectionModel(_slickSelectionModel);
        _slickgrid.registerPlugin(_slickCheckboxSelector);
        _slickgrid.registerPlugin(_slickgridHeaderMenu);
        _slickgrid.registerPlugin(_printPlugin);

        _$grid.css({overflow: "visible"})
        _$grid.find(".slick-viewport").css({overflow: "visible"})

        //expose the object so we can access it on custom js and access slickgrid events
        //https://stackoverflow.com/questions/5573806/is-there-a-way-to-get-an-instance-of-slickgrid-from-an-element
        _$grid.data("slickgridObject",_slickgrid);
    };

    var _buildViewDialogs = function() {
        _$viewSaveConfirmDialog = $(tmpl_view_save());

        _$viewSaveName = $('.js-view-name', _$viewSaveConfirmDialog);
    };

    var _buildViewDropdown = function(views) {
        _$viewDropdownList.empty();

        views.all.forEach(function(view) {
            _$viewDropdownList.append(tmpl_view_dropdown(view));
        });

        _addTooltipsToViewActions();
        _setActiveView(views.active);
    };

    var _prepareData = function(visibleData) {
        var addNoMatchFoundRow = (visibleData.length === 0);
        var addMoreRecordsRow = (!addNoMatchFoundRow && (visibleData.length < _totalResults));

        if (addNoMatchFoundRow) {
            visibleData.push({"nodata": true });
        }

        if (addMoreRecordsRow) {
            visibleData.push({"moreRecords": true });
        }
    };

    var _prepareRowStyling = function(visibleData) {

        visibleData.getItemMetadata = function(row) {

            if (visibleData[row].nodata) {
                return {
                    "cssClasses": "no-match-found-row",
                    "selectable": false,
                    "columns": {
                        0: {
                            colspan: "*",
                            formatter: function() {
                                return "<span class='no-match-found-text'>" + noResultsText + "</span>";
                            }
                        }
                    }
                };

            } else if (visibleData[row].moreRecords) {
                return {
                    "cssClasses": "more-records-row",
                    "selectable": false,
                    "columns": {
                        0: {
                            colspan: "*",
                            formatter: function() {
                                return "<span class='link more-records-text js-more-records'>"
                                           + sprintf(nextRecordsText, _pageSize) + "</span>";
                            }
                        }
                    }
                };

            } else if (visibleData[row].inactive) {
                return {
                    cssClasses: "inactive"
                };
            }

            return undefined;
        }
    };

    var _getCurrentSelectedRows = function() {
        var currentSelectedRows = _slickSelectionModel.getSelectedRows(),
            lastSelectedRow = _slickgrid.getDataItem(currentSelectedRows[currentSelectedRows.length - 1]);

        // Remove the last row if it's either the more records or the no data row
        // We do this because when selecting all rows on SlickGrid it silently selects those rows
        // even if they're set as not selectable
        if (lastSelectedRow && (lastSelectedRow.moreRecords || lastSelectedRow.nodata)) {
            currentSelectedRows.pop();
        }

        return currentSelectedRows;
    };

    var _getSelectedResultsIds = function() {
        var currentSelectedRows = _getCurrentSelectedRows(),
            selectedSearchResult = [],
            length = currentSelectedRows.length,
            i;

        for (i = 0; i < length; i++) {
            const entityId = _slickgrid.getDataItem(currentSelectedRows[i]).id;

            if(entityId !== undefined){
                selectedSearchResult.push(entityId);
            }
        }

        return selectedSearchResult;
    };

    var _setSelectedResults = function(resultsId) {
        var rows = _slickgrid.getData(),
            outLength = resultsId.length,
            inLength = rows.length,
            selectedRows = [],
            i, j;

        for (i = 0; i < outLength; i++) {
            for (j = 0; j < inLength; j++) {
                if (rows[j].id === resultsId[i]) {
                    selectedRows.push(j);
                }
            }
        }

        _slickSelectionModel.setSelectedRows(selectedRows);
    };

    var _selectAllResults = function() {
        var rows = [];
        for (var i = 0; i < _slickgrid.getDataLength(); i++) {
            rows.push(i);
        }
        _slickgrid.setSelectedRows(rows);
    };

    var _showData = function(visibleData) {
        var selectedResults;

        _visibleDataLength = (visibleData || []).length;

        if (_allInstancesSelected) {
            selectedResults = "all";

        } else {
            selectedResults = _getSelectedResultsIds();
        }

        _prepareData(visibleData);
        _prepareRowStyling(visibleData);

        _slickgrid.setData(visibleData);

        core.translate("recordm.search", _$gridCanvas);

        // The slickgrid is beeing initiated here because it has the property explicitInitialization set to 'true'.
        // It is necessary only when we are showing the grid for the first time, because in that process it will calculate
        // the width and height available for the component.
        // I couldn't find any problem in beeing executed on every request.
        _slickgrid.init();

        if (selectedResults !== "all") {
            _setSelectedResults(selectedResults);

        } else {
            _selectAllResults();
        }
    };

    var _showTotalMatchingRecords = function(total) {
        _totalResults = total;
        _$totalRecordsLabel.text(formatNumber(total, false, 0, core.getLocale()));

        if (_totalResults > 0) {
            _$actionsMsg.removeClass("none-pull-left");
            _$actionsMsg.text(selectEntriesText);

        } else {
            _$actionsMsg.addClass("none-pull-left");
            _$actionsMsg.text(noSelectableEntriesText);
        }
    };

    var _setViewStatus = function(text) {
        _$viewStatus.text(text).show();
        _$viewSaveButton.show();
    };

    var _resetViewStatus = function() {
        _$viewStatus.hide();
        _$viewSaveButton.hide();
    };

    var _showViewSaveDialog = function(name) {
        var $controlGroup = _$viewSaveName.parents('.js-control-group').first();

        $controlGroup.removeClass("error");
        $(".js-errormsg", $controlGroup).html("");

        _$viewSaveName.val(name);

        _$viewSaveConfirmDialog.modal({show: true, cobAppInstanceId: core.getAppInstanceId()});
        _$viewSaveName.focus();
    };

    var _setActiveView = function(activeView) {
        _$activeView.text(activeView.label || "Nenhuma");
    };

    var _changeViewSharingOption = function(viewId, isSharedView){
        var $view = $(".view-entry[data-viewId="+ viewId +"]", _$viewDropdownList),
            shareViewTooltip = core.translateString("recordm.search", "visualization.options.share"),
            unshareViewTooltip = core.translateString("recordm.search", "visualization.options.unshare"),
            $sharingBtn;

        if(isSharedView){
            $sharingBtn = $(".js-share-view", $view);

            $sharingBtn.addClass("js-unshare-view public-view");
            $sharingBtn.removeClass("js-share-view private-view");

            $sharingBtn.tooltip("destroy");
            $sharingBtn.tooltip({
                title: unshareViewTooltip,
                container:'#'+core.getAppInstanceId(),
                html:true
            });

        } else {
            $sharingBtn = $(".js-unshare-view", $view);

            $sharingBtn.addClass("js-share-view private-view");
            $sharingBtn.removeClass("js-unshare-view public-view");

            $sharingBtn.tooltip("destroy");
            $sharingBtn.tooltip({
                title: shareViewTooltip,
                container:'#'+core.getAppInstanceId(),
                html:true
            });
        }
    };

    var _addTooltipsToViewActions = function(){
        var deleteViewTooltip = core.translateString("recordm.search", "visualization.options.delete"),
            shareViewTooltip = core.translateString("recordm.search", "visualization.options.share"),
            unshareViewTooltip = core.translateString("recordm.search", "visualization.options.unshare");

        $(".js-delete-view", _$viewDropdownList).tooltip({
            title: deleteViewTooltip,
            container:'#'+core.getAppInstanceId()
        });

        $(".js-share-view", _$viewDropdownList).tooltip({
            title: shareViewTooltip,
            container:'#'+core.getAppInstanceId(),
            html:true
        });

        $(".js-unshare-view", _$viewDropdownList).tooltip({
            title: unshareViewTooltip,
            container:'#'+core.getAppInstanceId(),
            html:true
        });
    };

    var _updateMenuItemsSelection = function(selected) {
        var selectableMenuItems = _slickgridHeaderMenu.getSelectableItems(),
            menuItem;

        for (var i = 0; i < selectableMenuItems.length; i++) {
            menuItem = $(selectableMenuItems[i]);

            _slickgridHeaderMenu.setMenuItemSelection(menuItem.val(), selected);
        }
    };

    const _updateBatchActions = function(batchActions, customBatchActions) {
        _batchActions = batchActions || [];
        _customBatchActions = customBatchActions || [];

        const _groupedBatchActions = _customBatchActions
            .filter((a) => a.group)
            .reduce( (p, c) => {
                p[c.group] = p[c.group] || [];
                p[c.group].push(c);
                return p;
            }, {});
        const _topBatchActions = _customBatchActions.filter((a) => typeof a.group === 'undefined');

        _$batchActionsContainer.html(
            tmpl_batch_actions({
                batchActions: _batchActions,
                customBatchAction:_topBatchActions,
                groupedBatchActions: _groupedBatchActions,
                totalResults: "(" + _totalResults + ")"
            })
        );

        core.translate("recordm.search", _$batchActionsContainer);

        _updateVisibilityBatchSelectAllLink();
        _updateBatchActionsInstanceCounter();
    };

    var _updateVisibilityBatchSelectAllLink = function(){
        _$batchActionsContainer.find(".js-select-all-container").toggleClass("hidden", _allInstancesSelected);
        _$batchActionsContainer.find(".js-clear-all-container").toggleClass("hidden", !_allInstancesSelected);
    };

    const _getAllowedInstances = function (action, selectedEntities) {
        let totalAllowed = selectedEntities.filter(function(id) {
            return callbacks.canExecuteBatchAction(action, id);
        }).length;

        if(_allInstancesSelected){
            totalAllowed = allActionsText;
        }

        return totalAllowed;
    };

    const _changeActionState = function (action, totalAllowed, numVisibleSelectedEntities) {
        const $actionBtn = _$batchActionsContainer.find("*[data-action-key='" + action.key + "']");

        const actionAllowed = (totalAllowed === numVisibleSelectedEntities || totalAllowed === allActionsText);

        // custom batch actions are menu entries, the others are buttons
        if($actionBtn.is('a')){
            if(actionAllowed) $actionBtn.parent().removeClass('disabled')
            else $actionBtn.parent().addClass('disabled');
        }

        $actionBtn.prop("disabled", !actionAllowed)
            .tooltip({
                container: _$batchActionsContainer,
                trigger: 'hover',
                delay: {show: 1000},
                placement: "bottom",
                title: core.translateString("recordm.search", "bulkrequest.search-msg")
            });
    };

    const _addAllowedInstances = function (action, totalAllowed) {
        const $actionBtn = _$batchActionsContainer.find("*[data-action-key='" + action.key + "']"),
            $actionBtnLabel = $actionBtn.find(".js-action-label");

        $actionBtnLabel.html(action.label + " <span class='link'>(" + totalAllowed + ")</span>");
    };

    var _updateBatchActionsInstanceCounter = function() {
        var selectedEntities = _getSelectedResultsIds();
        var numVisibleSelectedEntities = selectedEntities.length;
        var numSelectedEntries = (_allInstancesSelected ? _totalResults : numVisibleSelectedEntities);

        if(_canPrint){
            _$printBtn.find(".js-total-selected")
                .text("("+formatNumber(numVisibleSelectedEntities, false, 0, core.getLocale())+")");
        }

        _$batchActionsContainer.find(".js-total-selected")
            .text(formatNumber(numSelectedEntries, false, 0, core.getLocale()));

        // For each batch action update the counter
        // associated to the entities selected that
        // the user is authorized to execute
        _batchActions.forEach(function(action) {
            const totalAllowed = _getAllowedInstances(action, selectedEntities);

            _changeActionState(action, totalAllowed, numVisibleSelectedEntities);
            _addAllowedInstances(action, totalAllowed);
        });

        if(_customBatchActions.length > 0){
            _customBatchActions.forEach(function(action) {
                const totalAllowed = _getAllowedInstances(action, selectedEntities);
                _changeActionState(action, totalAllowed, numVisibleSelectedEntities);
            });

            let moreActionsCounter = numVisibleSelectedEntities;

            if(_allInstancesSelected){
                moreActionsCounter = allActionsText;
            }

            const $moreBatchActionsBtnText = $(".js-custom-batch-actions-container-text");
            $moreBatchActionsBtnText.html(moreBatchActionsText + " <span class='link'>(" + moreActionsCounter + ")</span>");
        }
    };


    // listeners
    var _addListenerForInstanceActions = function() {
        _$gridCanvas.on('click', '.js-action', function(e) {
            e.preventDefault();

            /* eslint-disable-next-line no-invalid-this  */
            var $action = $(this);

            if (callbacks.executeInstanceAction) {
                callbacks.executeInstanceAction(
                    $action.data("action-key"),
                    $action.data("instancekey"),
                    $action.data("instanceid")
                );
            }
        });
    };

    var _addListenerForInstanceDetails = function() {
        if(!(callbacks.onDetailsClicked)) return;

        const clickOverride = function(e) {
            if (e.ctrlKey || e.metaKey) {
                //ctrlKey to detect ctrl+click and metaKey to detect cmd+click on MacOS
                //let it go normally
                return;
            }

            /* eslint-disable-next-line no-invalid-this  */
            var $action = $(this);

            callbacks.onDetailsClicked(e.target, $action.data("entityid"));

            return false;
        };
        _$gridCanvas.on('click', '.js-show-instance', clickOverride);
        _$gridCanvas.on('click', '.js-field-id-cell', clickOverride);
    };

    var _addListenerToBatchActions = function() {
        _$batchActionsContainer.on('click', "*[data-action-key]", function() {
            /* eslint-disable-next-line no-invalid-this  */
            var bActionKey = $(this).data("action-key");

            if (_allInstancesSelected) {
                callbacks.executeBatchActionOnQuery(bActionKey);
            } else {
                callbacks.executeBatchActionOnSelection(bActionKey, _getSelectedResultsIds());
            }
        });
    };

    var _addListenerToSelectAllAction = function() {
        _$batchActionsContainer.on("click", ".js-select-all", function() {
            _allInstancesSelected = true;
            _selectAllResults();
        });
    };

    var _addListenerToClearAllSelection = function() {
        _$batchActionsContainer.on("click", ".js-clear-selection", function() {
            _setSelectedResults([]);
        });
    };

    var _addListenerToMoreRecordsRequest = function() {
        _$grid.on("click", ".js-more-records", callbacks.moreRecordsClick);
    };

    var _addListenerToSelectionChanged = function() {
        _slickSelectionModel.onSelectedRangesChanged.subscribe(function() {
            var numSelectedEntities = _getSelectedResultsIds().length;

            if(_totalResults > 0 && numSelectedEntities === _totalResults && _totalResults !== _visibleDataLength){
                _allInstancesSelected = true;
            } else if(numSelectedEntities < _visibleDataLength){
                _allInstancesSelected = false;
            }

            if (numSelectedEntities > 0) {
                if(_canPrint) _$printBtn.removeClass("hidden");

                // Show batch actions
                _$batchActionsContainer.removeClass("hidden");
                _$actionsMsg.addClass("hidden");
            } else {
                if(_canPrint) _$printBtn.addClass("hidden");

                // Hide batch actions
                _$batchActionsContainer.addClass("hidden");
                _$actionsMsg.removeClass("hidden");
            }

            _updateVisibilityBatchSelectAllLink();
            _updateBatchActionsInstanceCounter();

            if(callbacks.selectionChanged) callbacks.selectionChanged(_allInstancesSelected);
        });
    };

    var _addListenerToColumnsResized = function() {
        _slickgrid.onColumnsResized.subscribe(function() {

            var cols = _slickgrid.getColumns();

            cols.forEach(function(col) {
                if (col.previousWidth !== col.width) {
                    callbacks.columnWidthChanged(col.field, col.width);
                }
            });
        });
    };

    var _addListenerToColumnsReordered = function() {
        _slickgrid.onColumnsReordered.subscribe(function() {
            var cols = _slickgrid.getColumns(),
                columns = [];

            for (var i = 0; i < cols.length - 1; i++) {
                columns.push({
                    fieldName: cols[i].field
                });
            }

            callbacks.columnOrderChanged(columns);
        });
    };

    var _addListenerToColumnsSorted = function() {
        _slickgrid.onSort.subscribe(function(e, args) {
            callbacks.sortColumns(args.sortCol.field, args.sortAsc);
        });
    };

    var _resetColumnSort = function() {
        _slickgrid.setSortColumns([]);
    };

    var _addListenerToViewDropdown = function() {
        _$viewDropdownList.on('click', '.js-view-name-container', function(e) {
            e.preventDefault();

            /* eslint-disable-next-line no-invalid-this  */
            var element = $(this),
                viewId = element.parents("li").attr('data-viewId'),
                viewLabel = element.text();

            callbacks.activateView(viewId, viewLabel);
        });

        _$viewDropdownList.on('click', '.js-delete-view', function(e) {
            e.preventDefault();

            /* eslint-disable-next-line no-invalid-this  */
            var element = $(this);
            callbacks.deleteView(element.parents("li").attr('data-viewId'));
        });

        _$viewDropdownList.on('click', '.js-share-view', function(e) {
            e.preventDefault();

            //Dont propagate the event to keep the dropdown open
            e.stopPropagation();

            /* eslint-disable-next-line no-invalid-this  */
            var element = $(this);
            callbacks.shareView(element.parents("li").attr('data-viewId'));
        });

        _$viewDropdownList.on('click', '.js-unshare-view', function(e) {
            e.preventDefault();

            //Dont propagate the event to keep the dropdown open
            e.stopPropagation();

            /* eslint-disable-next-line no-invalid-this  */
            var element = $(this);
            callbacks.unshareView(element.parents("li").attr('data-viewId'));
        });
    };

    var _addListenerToViewSaveButton = function() {
        _$viewSaveButton.click(function(e) {
            e.preventDefault();

            callbacks.saveCurrentView();
        });
    };

    var _addListenerToSaveDialog = function() {
        var cb = function(e) {
            var viewName = _$viewSaveName.val(),
                $controlGroup = _$viewSaveName.parents('.js-control-group').first();

            e.preventDefault();

            if (viewName && viewName !== "") {
                callbacks.viewNameConfirmed(_$viewSaveName.val());

                //todo jone ver se um detach aqui corrige VisualizationsTest.vis_save_is_successfull
                _$viewSaveConfirmDialog.modal("hide");
            } else {
                $controlGroup.addClass("error");

                $(".js-errormsg", $controlGroup).html(
                    core.translateString("recordm.search", "visualization.name-cannot-be-empty")
                );
            }
        };

        $(".js-submit", _$viewSaveConfirmDialog).on('click', cb);
        $("form", _$viewSaveConfirmDialog).on('submit', cb);
    };

    const _addListenerToImportButtons = function (){
        const $importersDropdown = $(".js-importers-dropdown-list", $container);

        $($importersDropdown).click(".js-importer-btn", function (e){
            e.stopPropagation();

            const $btn = $(e.target);
            const importerName = $btn.data("name");
            const importerLabel = $btn.data("label");
            const customMsg = $btn.data("message");
            const type = $btn.data("type");

            callbacks.importRecordsRequested(importerLabel, importerName, customMsg, type);
        });
    };

    // Throttle the resize event to avoid slowdowns
    var _onResize = $.throttle(function() {
        _slickgrid.resizeCanvas();
    }, 100);

    (function init() {
        var $html = $(tmpl_results({
            canPrint: _canPrint,
            canCreate: _canCreateNewRecord,
            canImport: _canImportRecords,
            customImports: confs.customImports
        }));

        core.translate("recordm.search", $html);

        $container.html($html);

        _populateVariables();

        _buildSlickGrid();
        _buildViewDialogs();

        _addListenerForInstanceActions();
        _addListenerToBatchActions();
        _addListenerToSelectAllAction();
        _addListenerToClearAllSelection();

        _addListenerToMoreRecordsRequest();
        _addListenerToSelectionChanged();
        _addListenerToColumnsResized();
        _addListenerToColumnsReordered();
        _addListenerToColumnsSorted();

        _addListenerToViewDropdown();
        _addListenerToViewSaveButton();
        _addListenerToSaveDialog();

        if(_canImportRecords && confs.customImports.length > 0){
            _addListenerToImportButtons();
        }

        if (_canCreateNewRecord) {
            _$newRecordBtn.on("click", callbacks.createNewRecordRequested);
        }

        if(_canImportRecords){
            _$importBtn.on("click", function (){
                callbacks.importRecordsRequested();
            });
        }

        if(_canPrint){
            _$printBtn.on('click', function () {
                var css = Array.prototype.slice.call(window.document.getElementsByTagName("link"))
                    .filter(function(el) {
                        return el.rel == 'stylesheet' &&
                               (el.href.indexOf('cob-') != -1 ||
                                el.href.indexOf('recordm-') != -1 ||
                                el.href.indexOf('localresource') != -1)

                    })
                    .map(function(link){return  $('<div>').append($(link).clone()).html();})
                    .join(' ');

                _printPlugin.print({
                    triggerPrintDialog: true,
                    cssLinksHtml: css,
                    title: definitionM.getName(),
                    selectedOnly:true
                });
            });
        }

        if(callbacks.onDetailsClicked){
            _addListenerForInstanceDetails();
        }

    })();

    return {
        start: function() {
            $(window).on('resize', _onResize);
            _slickgrid.resizeCanvas();

            // remove all tooltip content off the batch actions buttons,
            // because there is a glitch when  we leave and return to the module
            if(_$batchActionsContainer){
                _$batchActionsContainer.find(".tooltip").remove();
            }

            // Slickgrid does not calculate widths correctly if hidden (e.g. when not expanded)
            // we force the column calculation everytime if becomes visible
            if(typeof intersectionObserver === 'undefined') intersectionObserver = new IntersectionObserver(function(entries){
               entries.forEach((e) => {
                  if(e.intersectionRatio > 0){
                     _slickgrid.setColumns(_getColumnsInSlickgridFormat());
                  }
               })
            })

           intersectionObserver.observe($container[0])
        },
        stop: function() {
            $(window).off('resize', _onResize);
            intersectionObserver.disconnect();
        },
        setTotalMatchingResults: function(total) {
            _showTotalMatchingRecords(total);
        },
        setColumns: function(columns) {
            _columns = columns;

            // Reset the columns
            _slickgrid.setColumns(_getColumnsInSlickgridFormat());

            if (columns.sort) {
                _slickgrid.setSortColumn(columns.sort.column, columns.sort.ascending);

            } else {
                _slickgrid.setSortColumns([]);
            }

            // Reset the selected rows since slickgrid doesn't do it itself
           const selected = _slickSelectionModel.getSelectedRows()
           if( selected.length > 0) _slickSelectionModel.setSelectedRows(selected);
        },
        setSortColumn: function(fieldName, asc) {
            _slickgrid.setSortColumn(fieldName, asc || false);
        },
        setViewList: _buildViewDropdown,
        setViewStatus: _setViewStatus,
        resetViewStatus: _resetViewStatus,
        askForViewName: _showViewSaveDialog,
        setActiveView: _setActiveView,
        removeView: function(viewId) {
            _$viewDropdownList.find(".view-entry[data-viewId='" + viewId + "']").remove();
        },
        changeViewShareOption: _changeViewSharingOption,
        updateBatchActions: _updateBatchActions,
        resetView: function() {
            _slickgrid.setData([]);
            _slickSelectionModel.setSelectedRows([]);
            _slickgrid.init();
            _slickgrid.render();
            _totalResults = 0;
            _$totalRecordsLabel.text("--");
            _resetColumnSort();
        },
        showWatermark: function() {
            _$emptyResultMsgContainer.show();
        },
        getSelectedResultsIds: _getSelectedResultsIds,
        setSearchInProgressMode: function() {
            _slickgrid.setData([]);
        },
        showData: function(data) {
            _$emptyResultMsgContainer.hide();

            _showData(data);
            _slickgrid.resizeCanvas();
        },
        refreshGrid: function() {
            //hack para obrigar a grid a ser mostrada completamente qd se vem do editar/visualizar
            _slickgrid.resizeCanvas();
            _slickgrid.scrollRowIntoView(0);
        }
    };
};

export default DefinitionSearchResultV;
